import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions, storage } from '../lib/firebase';

/** The routing v2 classification constraints a rotation rule may carry. */
const ROTATION_CLASSIFICATION_KEYS = ['loanPurpose', 'propertyUse', 'borrowerSituation', 'loanStructure', 'loanAmount'] as const;
type RotationClassificationKey = (typeof ROTATION_CLASSIFICATION_KEYS)[number];

/**
 * Carry stored classification constraints onto rotations that do not mention
 * them. The setup wizard edits service / deal type / office only and sends
 * each rotation's match without the five classification arrays; the save is
 * a merge of the whole rotations array, so without this a wizard save would
 * silently drop a rule's classification and widen it to every lead of its
 * service. A rotation that names any of the keys is sent as given (the
 * Routing Rules page owns those edits). Pure.
 */
export function preserveRotationClassification(
  rotations: WizardRotationRule[],
  stored: Array<{ id?: unknown; match?: Record<string, unknown> }> | null | undefined,
): WizardRotationRule[] {
  if (!stored || stored.length === 0) return rotations;
  const byId = new Map(stored.filter((r) => typeof r.id === 'string').map((r) => [r.id as string, r.match ?? {}]));
  return rotations.map((r) => {
    const prior = byId.get(r.id);
    if (!prior) return r;
    if (ROTATION_CLASSIFICATION_KEYS.some((k) => k in r.match)) return r;
    const carried: Partial<Record<RotationClassificationKey, string[]>> = {};
    for (const k of ROTATION_CLASSIFICATION_KEYS) {
      const v = prior[k];
      if (Array.isArray(v) && v.length > 0) carried[k] = v.filter((x): x is string => typeof x === 'string');
    }
    return Object.keys(carried).length > 0 ? { ...r, match: { ...r.match, ...carried } } : r;
  });
}

export type WizardStepId =
  | 'branding'
  | 'resend'
  | 'domain'
  | 'contacts'
  | 'ad_accounts'
  | 'mycrm'
  | 'webhooks'
  | 'routing'
  | 'go_live';

export interface WizardStepState {
  completed: boolean;
  completedAt: string | null;
  completedBy: string | null;
}

export interface BrandingState {
  logoUrl: string | null;
  brandColor: string | null;
  companyName: string | null;
  abn: string | null;
  address: string | null;
  phone: string | null;
  /** eDM Reply-To. Required before an eDM can be scheduled. */
  replyToEmail: string | null;
  social: {
    facebook: string | null;
    instagram: string | null;
    linkedin: string | null;
    website: string | null;
  };
}

export interface OnboardingState {
  steps: Record<WizardStepId, WizardStepState>;
  branding: BrandingState;
  progressPercent: number;
}

export interface GoLiveChecklistItem {
  key: string;
  label: string;
  completed: boolean;
  detail?: string | null;
  /**
   * Only required items decide readiness. MyCRM and an ad platform are
   * informational: a business capturing leads from website forms alone uses
   * neither, and while they were mandatory such a tenant could never reach
   * "ready to go live".
   */
  required?: boolean;
}

export interface GoLiveChecklistResult {
  checklist: GoLiveChecklistItem[];
  /** Completed REQUIRED items. */
  completedCount: number;
  /** Total REQUIRED items, so the count agrees with `readyToGoLive`. */
  totalCount: number;
  optionalCompletedCount?: number;
  optionalCount?: number;
  readyToGoLive: boolean;
}

export interface ResendDomainRecord {
  record?: string;
  name?: string;
  type?: string;
  ttl?: string | number;
  status?: string;
  value?: string;
  priority?: number;
  [k: string]: unknown;
}

export interface ResendDomainCheckResult {
  success: boolean;
  verified: boolean;
  status: string;
  resendDomainId: string | null;
  records: ResendDomainRecord[];
}

export interface MonthlyValueReport {
  monthStart: string;
  leadsCount: number;
  emailsSentCount: number;
  alertsCaughtCount: number;
  attributionCoveragePercent: number;
}

export type WizardRotationStrategy =
  | 'round_robin'
  | 'least_loaded'
  | 'random'
  | 'first_available';

/**
 * A single rich rotation rule as accepted by the `saveLeadRoutingConfig`
 * action (mirrors `RotationRuleSchema` in `@valutten/shared-schemas`). Broker
 * ids are Firebase Auth UIDs (the id space `getBrokerLabels` + the server-side
 * `assigneeValidation` accept), NOT commission `broker_<hash>` doc ids. New
 * wizard rotations are service-only. Existing office/deal-type constraints
 * are preserved; edit those on the full Routing Rules page.
 */
export interface WizardRotationRule {
  id: string;
  name: string;
  match: {
    serviceCategory: string | null;
    dealType: string | null;
    officeId: string | null;
    // Routing v2 classification constraints. The wizard does not edit them;
    // saveLeadRoutingConfig carries stored values over (see
    // preserveRotationClassification).
    loanPurpose?: string[] | null;
    propertyUse?: string[] | null;
    borrowerSituation?: string[] | null;
    loanStructure?: string[] | null;
    loanAmount?: string[] | null;
  };
  strategy: WizardRotationStrategy;
  brokerIds: string[];
}

/**
 * Payload for `saveLeadRoutingConfig`. The wizard owns `rotations` and the
 * `notifyOnAssignment.email` toggle; every other global (quietHours, geoRules,
 * SLA defaults, cascadePriority…) is passed through verbatim from the stored
 * config doc so the wizard never clobbers settings owned by the full Lead
 * Routing Rules page. Server-managed keys (`rotationCursors`, `updatedAt`,
 * `updatedBy`) are stripped by the server-side `omit()` schema.
 */
export interface LeadRoutingConfigInput {
  rotations: WizardRotationRule[];
  notifyOnAssignment?: { email?: boolean; inApp?: boolean; sms?: boolean };
  [key: string]: unknown;
}

interface State {
  data: OnboardingState | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  markStepComplete: (stepId: WizardStepId, completed: boolean) => Promise<void>;
  saveBranding: (args: {
    brandColor?: string;
    companyName?: string;
    abn?: string;
    /** The tenant's declaration that `abn` is their business's ABN (required with a new ABN). */
    abnDeclaration?: boolean;
    address?: string;
    phone?: string;
    /** '' clears the stored address; undefined leaves it unchanged. */
    replyToEmail?: string;
    socialFacebook?: string;
    socialInstagram?: string;
    socialLinkedin?: string;
    socialWebsite?: string;
  }) => Promise<void>;
  /**
   * @param expectedUpdatedAtMillis Optimistic lock. The server compares it
   * against the stored `updatedAt` and throws 'aborted' on a mismatch, which
   * is what stops one surface clobbering a concurrent edit made on another
   * (the wizard and the Routing Rules page write the same document). Pass
   * null when the document does not exist yet.
   */
  saveLeadRoutingConfig: (
    config: LeadRoutingConfigInput,
    expectedUpdatedAtMillis?: number | null
  ) => Promise<void>;
  generatePerOfficeToken: (officeId: string) => Promise<string>;
  generateSourceToken: (source: string) => Promise<string>;
  saveResendApiKey: (apiKey: string) => Promise<{ secretVersionName: string }>;
  checkResendDomain: (domain: string) => Promise<ResendDomainCheckResult>;
  getGoLiveChecklist: () => Promise<GoLiveChecklistResult>;
  getMonthlyValueReport: () => Promise<MonthlyValueReport>;
  uploadLogo: (file: File) => Promise<string>;
}

export function useLeadCaptureOnboarding(
  companyId: string | undefined
): State {
  const [data, setData] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const fn = httpsCallable<
      { action: 'getOnboardingState'; companyId: string },
      OnboardingState
    >(functions, 'leadCaptureAction');
    setLoading(true);
    setError(null);
    fn({ action: 'getOnboardingState', companyId })
      .then((r) => setData(r.data))
      .catch((err) => {
        console.error('[useLeadCaptureOnboarding] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, refreshKey]);

  const markStepComplete = useCallback(
    async (stepId: WizardStepId, completed: boolean) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'markStepComplete';
          companyId: string;
          stepId: WizardStepId;
          completed: boolean;
        },
        { success: boolean }
      >(functions, 'leadCaptureAction');
      await fn({ action: 'markStepComplete', companyId, stepId, completed });
      refresh();
    },
    [companyId, refresh]
  );

  const saveBranding = useCallback(
    async (args: {
      brandColor?: string;
      companyName?: string;
      abn?: string;
      abnDeclaration?: boolean;
      address?: string;
      phone?: string;
      replyToEmail?: string;
      socialFacebook?: string;
      socialInstagram?: string;
      socialLinkedin?: string;
      socialWebsite?: string;
    }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'saveBranding';
          companyId: string;
          brandColor?: string;
          companyName?: string;
          abn?: string;
          abnDeclaration?: boolean;
          address?: string;
          phone?: string;
          replyToEmail?: string;
          socialFacebook?: string;
          socialInstagram?: string;
          socialLinkedin?: string;
          socialWebsite?: string;
        },
        { success: boolean }
      >(functions, 'leadCaptureAction');
      // Wire contract (owned by SaveBrandingSchema in leadCaptureAction.ts):
      // absent or null = unchanged, '' = clear. Callers send only the fields
      // they mean to change; the transport's undefined -> null is harmless.
      await fn({ action: 'saveBranding', companyId, ...args });
      refresh();
    },
    [companyId, refresh]
  );

  const saveLeadRoutingConfig = useCallback(
    async (
      config: LeadRoutingConfigInput,
      expectedUpdatedAtMillis?: number | null
    ) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'saveLeadRoutingConfig';
          companyId: string;
          config: LeadRoutingConfigInput;
          expectedUpdatedAtMillis?: number | null;
        },
        { success: boolean }
      >(functions, 'leadCaptureAction');
      // Classification constraints are not edited by the wizard: carry the
      // stored ones over so a wizard save never widens a routing v2 rule. A
      // failed read is fatal here rather than risking that widening.
      let storedRotations: Array<{ id?: unknown; match?: Record<string, unknown> }> = [];
      try {
        const snap = await getDoc(doc(db, `companies/${companyId}/lead_routing_rules/default`));
        storedRotations = (snap.data()?.rotations as typeof storedRotations | undefined) ?? [];
      } catch {
        throw new Error(
          'Could not read the current routing rules, so saving was blocked to avoid overwriting them. Reload the page and try again.'
        );
      }
      const rotations = preserveRotationClassification(config.rotations, storedRotations);
      // The lock is only sent when the caller supplies one. Omitting the key
      // entirely preserves the existing behaviour for callers that have no
      // read to lock against; the server treats `undefined` as "no check".
      await fn({
        action: 'saveLeadRoutingConfig',
        companyId,
        config: { ...config, rotations },
        ...(expectedUpdatedAtMillis !== undefined
          ? { expectedUpdatedAtMillis }
          : {}),
      });
      refresh();
    },
    [companyId, refresh]
  );

  const generatePerOfficeToken = useCallback(
    async (officeId: string) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'generatePerOfficeToken';
          companyId: string;
          officeId: string;
        },
        { success: boolean; token: string }
      >(functions, 'leadCaptureAction');
      const result = await fn({
        action: 'generatePerOfficeToken',
        companyId,
        officeId,
      });
      return result.data.token;
    },
    [companyId]
  );

  const generateSourceToken = useCallback(
    async (source: string) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        { action: 'generateSourceToken'; companyId: string; source: string },
        { success: boolean; token: string }
      >(functions, 'leadCaptureAction');
      const result = await fn({
        action: 'generateSourceToken',
        companyId,
        source,
      });
      return result.data.token;
    },
    [companyId]
  );

  const saveResendApiKey = useCallback(
    async (apiKey: string) => {
      if (!companyId) throw new Error('companyId required');
      // Standalone callable since 2026-08-25 (B1 credential-plane split); the
      // leadCaptureAction action:'saveResendApiKey' branch it replaced is
      // deprecated and removed at the client cut.
      const fn = httpsCallable<
        { companyId: string; apiKey: string },
        { success: boolean; secretVersionName: string }
      >(functions, 'saveResendApiKey');
      const result = await fn({
        companyId,
        apiKey,
      });
      refresh();
      return { secretVersionName: result.data.secretVersionName };
    },
    [companyId, refresh]
  );

  const checkResendDomain = useCallback(
    async (domain: string) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        { action: 'checkResendDomain'; companyId: string; domain: string },
        ResendDomainCheckResult
      >(functions, 'leadCaptureAction');
      const result = await fn({
        action: 'checkResendDomain',
        companyId,
        domain,
      });
      refresh();
      return result.data;
    },
    [companyId, refresh]
  );

  const getGoLiveChecklist = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    const fn = httpsCallable<
      { action: 'getGoLiveChecklist'; companyId: string },
      GoLiveChecklistResult
    >(functions, 'leadCaptureAction');
    const result = await fn({ action: 'getGoLiveChecklist', companyId });
    return result.data;
  }, [companyId]);

  const getMonthlyValueReport = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    const fn = httpsCallable<
      { action: 'getMonthlyValueReport'; companyId: string },
      MonthlyValueReport
    >(functions, 'leadCaptureAction');
    const result = await fn({ action: 'getMonthlyValueReport', companyId });
    return result.data;
  }, [companyId]);

  const uploadLogo = useCallback(
    async (file: File) => {
      if (!companyId) throw new Error('companyId required');
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const path = `companies/${companyId}/branding/logo.${ext}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file, {
        contentType: file.type || `image/${ext}`,
        customMetadata: {
          uploadedAt: new Date().toISOString(),
        },
      });
      const url = await getDownloadURL(ref);
      await httpsCallable<
        {
          action: 'saveBranding';
          companyId: string;
          logoUrl: string;
        },
        { success: boolean }
      >(functions, 'leadCaptureAction')({
        action: 'saveBranding',
        companyId,
        logoUrl: url,
      });
      refresh();
      return url;
    },
    [companyId, refresh]
  );

  return {
    data,
    loading,
    error,
    refresh,
    markStepComplete,
    saveBranding,
    saveLeadRoutingConfig,
    generatePerOfficeToken,
    generateSourceToken,
    saveResendApiKey,
    checkResendDomain,
    getGoLiveChecklist,
    getMonthlyValueReport,
    uploadLogo,
  };
}
